import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CreationLockResponse } from '../pages/shared/interfaces/creation-lock-response.interface';

interface CalcOfferSlice {
  premiumDiscounted?: number;
  premiumSuggested?: number;
  productName?: string;
}

interface GapNgxsState {
  offerState?: {
    offerLocked?: CreationLockResponse | null;
    offer?: CalcOfferSlice | null;
  };
  policyCreatorState?: {
    firstStepPayload?: { selectedInsuranceVariant?: string };
  };
  warrantyOfferState?: {
    warrantyOfferLocked?: CreationLockResponse | null;
    enabledPortfoliosWithOffers?: Array<{
      isSelected?: boolean;
      calculation?: CalcOfferSlice;
    }>;
  };
  warrantyCreatorState?: {
    firstStepPayload?: { selectedInsuranceVariant?: string };
  };
}
import {
  loadLockSnapshot,
  markLeadSent,
  saveLockSnapshot,
} from '../utils/analytics-lock-snapshot';
import { isOfferPaid } from '../utils/signature-state.util';
import { AnalyticsService } from './analytics.service';

@Injectable({ providedIn: 'root' })
export class AnalyticsPurchaseTrackerService implements OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private started = false;

  constructor(
    private readonly store: Store,
    private readonly analytics: AnalyticsService,
  ) {}

  start(): void {
    if (this.started || !this.analytics.isEnabled()) {
      return;
    }
    this.started = true;

    this.store
      .select((state: GapNgxsState) => state.offerState?.offerLocked)
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(
          (prev, curr) =>
            prev?.id === curr?.id &&
            prev?.signatureState === curr?.signatureState &&
            prev?.paidOn === curr?.paidOn,
        ),
      )
      .subscribe((offer) => this.handleOffer(offer, 'policy'));

    this.store
      .select(
        (state: GapNgxsState) => state.warrantyOfferState?.warrantyOfferLocked,
      )
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(
          (prev, curr) =>
            prev?.id === curr?.id &&
            prev?.signatureState === curr?.signatureState &&
            prev?.paidOn === curr?.paidOn,
        ),
      )
      .subscribe((offer) => this.handleOffer(offer, 'warranty'));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private handleOffer(
    offer: CreationLockResponse | null,
    flow: 'policy' | 'warranty',
  ): void {
    if (!offer?.number) {
      return;
    }

    this.persistLockSnapshot(offer, flow);

    const paid = isOfferPaid(offer);

    // Lock / redirect to /cta/upload happens before payment — lead only, never purchase.
    if (!paid) {
      if (markLeadSent(offer.number)) {
        this.analytics.generateLead({
          policy_no: offer.number,
          flow,
          product_code: offer.productCode,
        });
      }
      return;
    }

    const snapshot = loadLockSnapshot(offer.number);
    const premiumGrosze =
      snapshot?.premiumGrosze ?? offer.premiumSuggested;
    const variant = snapshot?.variant ?? offer.productCode;
    const productCode = snapshot?.productCode ?? offer.productCode;
    const itemName = snapshot?.productName ?? variant ?? productCode;
    const value = this.analytics.groszeToPln(premiumGrosze);

    this.analytics.purchase({
      transaction_id: offer.number,
      value,
      currency: 'PLN',
      items: [
        {
          item_id: productCode,
          item_name: itemName,
          item_variant: variant,
          price: value,
          quantity: 1,
        },
      ],
    });
  }

  private persistLockSnapshot(
    offer: CreationLockResponse,
    flow: 'policy' | 'warranty',
  ): void {
    const state = this.store.snapshot() as GapNgxsState;
    const calcOffer =
      flow === 'policy'
        ? state.offerState?.offer
        : state.warrantyOfferState?.enabledPortfoliosWithOffers?.find(
            (portfolio) => portfolio.isSelected,
          )?.calculation;

    const variant =
      flow === 'policy'
        ? state.policyCreatorState?.firstStepPayload?.selectedInsuranceVariant
        : state.warrantyCreatorState?.firstStepPayload
            ?.selectedInsuranceVariant;

    const existing = loadLockSnapshot(offer.number);
    const premiumGrosze =
      calcOffer?.premiumDiscounted ??
      calcOffer?.premiumSuggested ??
      existing?.premiumGrosze ??
      offer.premiumSuggested;

    saveLockSnapshot({
      policyNo: offer.number,
      flow,
      premiumGrosze: premiumGrosze ?? 0,
      variant: variant || existing?.variant || offer.productCode,
      productCode: offer.productCode || existing?.productCode || '',
      productName:
        calcOffer?.productName ||
        existing?.productName ||
        variant ||
        offer.productCode,
    });
  }
}
